import { type KeyboardEvent, useLayoutEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { Route } from "@/api/models"

import { CircleCheck, LayoutGrid, Search, SearchX } from "@/components/icons"
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer"
import { Input } from "@/components/ui/input"
import { NodePill } from "@/components/ui/node-pill"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"

import { ModuleIcon } from "./module-icon"

/** A module rendered as a gallery card. Built from a Route, plus a synthetic
 *  Training entry that has no Route of its own. */
type GalleryItem = {
  key: string
  id: string
  name: string
  description: string
  path: string
  route?: Route
  isBeta?: boolean
  /** Small red dot indicator (used for incomplete training). */
  showAlertDot?: boolean
}

type ModuleGalleryProps = {
  /** Enabled routes, already ordered for display. */
  routes: Route[]
  activeLink: string
  trainingEnabled: boolean
  hasIncompleteTraining: boolean
  incompleteTrainingLabel: string
  /** Localizes a route's info_text (translation key or per-language object). */
  getInfoText: (info_text: Route["info_text"] | undefined) => string
  onNavigate: (path: string) => void
}

export function ModuleGallery({
  routes,
  activeLink,
  trainingEnabled,
  hasIncompleteTraining,
  incompleteTrainingLabel,
  getInfoText,
  onNavigate,
}: ModuleGalleryProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  // Match the repo's responsive picker convention (e.g. library-picker):
  // Popover on desktop, bottom Drawer on mobile. Ignore the content sidebar so
  // opening a right panel doesn't turn the header switcher into a drawer.
  const isMobile = useIsMobile({ breakpoint: 768, includeContentSidebarOpen: false })

  // Drawer (mobile) and Popover (desktop) share one open state. If the viewport
  // crosses the breakpoint while open, close before paint (useLayoutEffect, not
  // useEffect) so the newly-mounted surface does not flash for one frame.
  const prevIsMobileRef = useRef(isMobile)
  useLayoutEffect(() => {
    if (prevIsMobileRef.current === isMobile) return
    prevIsMobileRef.current = isMobile
    if (open) {
      setOpen(false)
      setQuery("")
    }
  }, [isMobile, open])

  const searchLabel = t("pages.common.search_modules", { defaultValue: "Search apps…" })

  const items = useMemo<GalleryItem[]>(() => {
    const routeItems: GalleryItem[] = routes.map((route) => ({
      key: `${route.path}-${route.id}`,
      id: route.id,
      name: route.name,
      description: getInfoText(route.info_text),
      path: route.path,
      route,
      isBeta: route.path === "/flows",
    }))

    if (trainingEnabled) {
      routeItems.push({
        key: "training",
        id: "training",
        name: "AI Training",
        description: t("training.description", {
          defaultValue: "Access training materials and resources",
        }),
        path: "/training",
        showAlertDot: hasIncompleteTraining,
      })
    }

    return routeItems
  }, [routes, trainingEnabled, hasIncompleteTraining, getInfoText, t])

  const activeItem = useMemo(
    () => items.find((item) => item.name === activeLink),
    [items, activeLink]
  )
  const activeItemIsBeta = activeItem?.isBeta ?? false

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter(
      (item) => item.name.toLowerCase().includes(q) || item.description.toLowerCase().includes(q)
    )
  }, [items, query])

  const handleSelect = (path: string) => {
    setOpen(false)
    setQuery("")
    onNavigate(path)
  }

  // Restore the DropdownMenu's roving arrow-key focus on the card grid (the
  // Popover doesn't provide it). Column count is read from the live grid so it
  // tracks the responsive 1/2/3-col layout.
  const searchRef = useRef<HTMLInputElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  const getCards = () =>
    Array.from(gridRef.current?.querySelectorAll<HTMLButtonElement>("[data-gallery-card]") ?? [])

  const getColumnCount = () => {
    if (!gridRef.current) return 1
    const cols = getComputedStyle(gridRef.current).gridTemplateColumns.split(" ").filter(Boolean)
    return Math.max(1, cols.length)
  }

  const focusCardAt = (cards: HTMLButtonElement[], index: number) => {
    cards[Math.max(0, Math.min(index, cards.length - 1))]?.focus()
  }

  const handleGridKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const cards = getCards()
    const current = cards.indexOf(document.activeElement as HTMLButtonElement)
    if (cards.length === 0 || current === -1) return

    const cols = getColumnCount()
    switch (e.key) {
      case "ArrowRight":
        focusCardAt(cards, current + 1)
        break
      case "ArrowLeft":
        focusCardAt(cards, current - 1)
        break
      case "ArrowDown":
        focusCardAt(cards, current + cols)
        break
      case "ArrowUp":
        // From the top row, step back up into the search field.
        if (current < cols) searchRef.current?.focus()
        else focusCardAt(cards, current - cols)
        break
      case "Home":
        focusCardAt(cards, 0)
        break
      case "End":
        focusCardAt(cards, cards.length - 1)
        break
      default:
        return
    }
    e.preventDefault()
  }

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      getCards()[0]?.focus()
    }
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setQuery("")
  }

  const triggerButton = (
    <button
      className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground relative flex max-w-[260px] items-center gap-2 rounded-md py-1.5 pr-2 pl-1.5 whitespace-nowrap transition-colors"
      data-testid="module-selector"
      type="button"
    >
      <span className="bg-primary text-primary-foreground border-foreground flex size-6 shrink-0 items-center justify-center rounded-md border">
        <ModuleIcon className="size-4" fallbackId={activeItem?.id} route={activeItem?.route} />
      </span>
      <span className="min-w-0 truncate text-sm font-medium">{activeLink}</span>
      {activeItemIsBeta && (
        <NodePill brand className="shrink-0" size="sm" tone="primary">
          Beta
        </NodePill>
      )}
      <LayoutGrid className="text-muted-foreground size-3.5 shrink-0" />
      {hasIncompleteTraining && (
        <span className="absolute -top-0.5 -right-0.5 inline-flex h-2 w-2 items-center justify-center">
          <span className="sr-only">{incompleteTrainingLabel}</span>
          <span aria-hidden className="bg-destructive inline-flex h-2 w-2 rounded-full" />
        </span>
      )}
    </button>
  )

  const galleryBody = (
    <div className="flex flex-col">
      <div className="border-border border-b p-4">
        <div className="relative">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          {/* Radix focuses the first focusable element (this input) on open. */}
          <Input
            ref={searchRef}
            aria-label={searchLabel}
            className="pl-9"
            data-testid="module-gallery-search"
            placeholder={searchLabel}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
        </div>
      </div>

      <div className="max-h-[calc(70vh-7rem)] overflow-y-auto p-4">
        {filteredItems.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="bg-muted text-muted-foreground flex size-12 items-center justify-center rounded-lg">
              <SearchX className="size-6" />
            </div>
            <p className="text-muted-foreground text-sm">
              {t("pages.common.no_modules_found", {
                defaultValue: "No apps match your search.",
              })}
            </p>
          </div>
        ) : (
          <TooltipProvider delayDuration={200}>
            <div
              ref={gridRef}
              className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2 lg:grid-cols-3"
              role="presentation"
              onKeyDown={handleGridKeyDown}
            >
              {filteredItems.map((item) => {
                const isActive = item.name === activeLink
                return (
                  <Tooltip key={item.key}>
                    <TooltipTrigger asChild>
                      <button
                        data-gallery-card
                        data-testid={`module-item-${item.id}`}
                        type="button"
                        className={cn(
                          "card-interactive group bg-card text-card-foreground flex flex-col items-start gap-3 rounded-lg border p-4 text-left shadow-sm",
                          "active:scale-[0.98]",
                          "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                          isActive && "border-primary/40 bg-primary/5"
                        )}
                        onClick={() => handleSelect(item.path)}
                      >
                        <div className="flex w-full items-start justify-between">
                          <div className="bg-primary/10 text-primary border-border group-hover:bg-primary/20 flex size-10 shrink-0 items-center justify-center rounded-lg border transition-colors duration-200">
                            <ModuleIcon
                              className="size-5"
                              fallbackId={item.id}
                              route={item.route}
                            />
                          </div>
                          {isActive && <CircleCheck className="text-primary size-5 shrink-0" />}
                          {!isActive && item.showAlertDot && (
                            <span className="bg-destructive mt-1 size-2 shrink-0 rounded-full" />
                          )}
                        </div>

                        <div className="w-full min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="truncate text-base font-semibold">{item.name}</p>
                            {item.isBeta && (
                              <NodePill brand className="shrink-0" size="sm" tone="primary">
                                Beta
                              </NodePill>
                            )}
                          </div>
                          {/* On touch (Drawer) there is no hover, so show the
                              full description inline; on desktop it lives in the
                              tooltip below to keep cards compact. */}
                          {isMobile && item.description && (
                            <p className="text-muted-foreground mt-1 text-sm">{item.description}</p>
                          )}
                        </div>
                      </button>
                    </TooltipTrigger>
                    {!isMobile && item.description && (
                      <TooltipContent className="z-[110] max-w-[260px]" side="bottom">
                        {item.description}
                      </TooltipContent>
                    )}
                  </Tooltip>
                )
              })}
            </div>
          </TooltipProvider>
        )}
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
        <DrawerContent data-testid="module-gallery">
          <DrawerTitle className="sr-only">{t("pages.common.modules")}</DrawerTitle>
          {galleryBody}
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-[70vh] w-[min(92vw,640px)] overflow-hidden p-0"
        data-testid="module-gallery"
      >
        {galleryBody}
      </PopoverContent>
    </Popover>
  )
}
